import type { ReactNode } from 'react';

/**
 * The building blocks the Pro analytics cards on a tender page are made of —
 * the base-rates stat cells and price band, the concentration pill, tiles and
 * market-share bar.
 *
 * They live here because the LOCKED teaser has to be the SAME object as the
 * real card, only with its values blurred out (see `analytics-teaser-bodies`).
 * A copy-pasted teaser drifts the first time the real card is restyled, and
 * then the upsell shows a layout the buyer never actually gets — so both render
 * through these, and a class change lands on both at once.
 *
 * Nothing here knows about entitlement: a caller passes either a real value or
 * a blurred placeholder into the same slot.
 */

/** Two-up grid of `MetricCell`s — the base-rates «участники / медиана цены» row. */
export function MetricGrid({
  children,
  single = false,
}: {
  children: ReactNode;
  single?: boolean;
}) {
  // `single`: one cell spans the row instead of sitting in half of it beside a
  // blank (a metric the data does not have is not drawn — base-rates panel).
  return (
    <dl
      className={`grid ${single ? 'grid-cols-1' : 'grid-cols-2'} divide-x divide-edge-light border-t border-edge-light`}
    >
      {children}
    </dl>
  );
}

/** One base-rates stat cell: uppercase label, big number, optional basis line.
 *  The basis line is not decoration — an absolute median means nothing without
 *  the population it is a median OF. */
export function MetricCell({
  label,
  value,
  basis,
}: {
  label: ReactNode;
  value: ReactNode;
  basis?: ReactNode;
}) {
  return (
    <div className="p-4 sm:p-5">
      {/* Two lines are reserved for the label, always. «Медиана цены
          победителя» wraps where «Медиана участников» does not (and in Uzbek it
          is the other way round), which left the two values ~50 px apart
          vertically. A fixed label box puts them on one baseline in every
          locale, locked card and real card alike.
          3em, not 2: the inherited line-height here is 1.5, so two lines are
          2 × 1.5em = 33 px at this 11 px size. Reserving 2.4em left the
          single-line Uzbek label 7 px short of the wrapped one. */}
      <dt className="mb-1 min-h-[3em] text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
        {label}
      </dt>
      <dd className="text-[22px] font-bold tabular-nums text-ink">{value}</dd>
      {basis && <p className="mt-1 text-[11px] leading-snug text-ink-muted">{basis}</p>}
    </div>
  );
}

/** Winning-price band: gradient fill positioned at `fillPct`, with the p25/p75
 *  ends underneath. `low`/`high` are nodes so a locked card can hand in blurred
 *  placeholders instead of numbers. */
export function PriceBandBar({
  label,
  fillPct,
  low,
  high,
  footer,
}: {
  label: ReactNode;
  fillPct: number;
  low: ReactNode;
  high: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="border-t border-edge-light p-4 sm:p-5">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
        {label}
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-pill bg-bone">
        <div
          className="h-full rounded-pill bg-gradient-to-r from-brand/40 to-brand"
          style={{ width: `${fillPct}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-baseline justify-between text-[13px] font-semibold tabular-nums text-ink-soft">
        <span>{low}</span>
        <span>{high}</span>
      </div>
      {footer}
    </div>
  );
}

/** Concentration band (HHI thresholds, see `lib/concentration-read`) → pill
 *  colours: red / amber / green, the same at 5–9 and 10+ contracts. `moderate`
 *  was the neutral grey until 2026-10-10 and read as «судить рано»; the owner
 *  named four colours — red, yellow, green, grey — so it borrows the existing
 *  amber `status.closing-*` pair (no new token). `unknown` is the locked card's
 *  neutral: a teaser must not tint itself, because the tint alone would leak
 *  the verdict the card is selling. */
export const CONCENTRATION_PILL_CLASS = {
  high: 'border-rust/30 bg-orange/10 text-rust',
  moderate: 'border-status-closing-dot/40 bg-status-closing-bg text-status-closing-text',
  low: 'border-brand/20 bg-brand-dim text-brand',
  unknown: 'border-edge bg-bone text-ink-soft',
} as const;

export function ConcentrationPill({
  level,
  title,
  children,
}: {
  level: keyof typeof CONCENTRATION_PILL_CLASS;
  /** Hover text — the only place the index number may appear (Igor, 2026-10-07). */
  title?: string;
  children: ReactNode;
}) {
  return (
    // `overflow-hidden` for the locked card's sake: the blurred placeholder
    // inside it would otherwise haze across the pill's own border. A no-op for
    // the real pill, whose content is sharp and inside the box anyway.
    <span
      title={title}
      className={`overflow-hidden rounded-pill border px-2 py-0.5 text-[11px] font-bold leading-[1.5] ${CONCENTRATION_PILL_CLASS[level]}`}
    >
      {children}
    </span>
  );
}

/** Verdict tile on the concentration card (top-supplier share, top-3 share). */
export function ShareTile({
  label,
  value,
  basis,
}: {
  label: ReactNode;
  value: ReactNode;
  basis?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-edge-light bg-bone px-3 py-2.5">
      <div className="text-[11px] text-ink-muted">{label}</div>
      <div className="text-[20px] font-bold leading-none tabular-nums text-ink">{value}</div>
      {basis && <div className="mt-1 text-[10.5px] text-ink-muted">{basis}</div>}
    </div>
  );
}

/** Sequential teal ramp for the distribution bar + matching rank dots, darkest
 *  = largest share. Brand-token opacities (no raw hex — see apps/web/CLAUDE.md);
 *  the neutral «остальные» tail uses `bg-edge`. Index by rank. */
export const SEGMENT_BG = [
  'bg-brand',
  'bg-brand/75',
  'bg-brand/55',
  'bg-brand/40',
  'bg-brand/30',
] as const;

export interface ShareSegment {
  key: string;
  /** Tailwind background class — `SEGMENT_BG[rank]` or `bg-edge` for the tail. */
  className: string;
  widthPct: number;
  title?: string;
}

/** One distribution bar for the whole supplier mix — replaces the per-row bars
 *  so the market SHAPE reads at a glance. */
export function MarketShareBar({ segments }: { segments: ShareSegment[] }) {
  return (
    <div className="flex h-3 gap-[2px] overflow-hidden rounded-pill bg-edge-light">
      {segments.map((s) => (
        <div
          key={s.key}
          className={`h-full ${s.className}`}
          style={{ width: `${s.widthPct}%` }}
          title={s.title}
        />
      ))}
    </div>
  );
}

// ── Integrity card ────────────────────────────────────────────────────

/** The integrity card's four signal glyphs. */
export type IntegrityGlyph = 'relist' | 'person' | 'shield' | 'quals';

export function IntegrityIcon({ name, className }: { name: IntegrityGlyph; className?: string }) {
  const p = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
  };
  if (name === 'relist')
    return (
      <svg {...p}>
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        <path d="M3 21v-5h5" />
      </svg>
    );
  if (name === 'person')
    return (
      <svg {...p}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
      </svg>
    );
  if (name === 'shield')
    return (
      <svg {...p}>
        <path d="M12 3l7 3v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6z" />
      </svg>
    );
  return (
    <svg {...p}>
      <rect x="8" y="3" width="8" height="4" rx="1" />
      <path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  );
}

/** One signal — icon-chip + fact + evidence. `flag` = notable; `lead` = the
 *  strongest (re-listing), gets the deepest tint. */
export function SignalRow({
  icon,
  label,
  sub,
  flag = false,
  lead = false,
  detail,
  value,
}: {
  icon: IntegrityGlyph;
  label: string;
  sub?: string;
  flag?: boolean;
  lead?: boolean;
  /** Collapsed evidence under the row (the disqualifying criteria list). */
  detail?: ReactNode;
  /** Right-hand slot. The real card carries its facts in `label`/`sub`; the
   *  locked teaser puts a blurred pill here so the row still has a value shape. */
  value?: ReactNode;
}) {
  const hot = flag || lead;
  return (
    <li
      className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 ${
        lead
          ? 'border-rust/30 bg-orange/10'
          : flag
            ? 'border-rust/20 bg-orange/5'
            : 'border-edge-light bg-bone'
      }`}
    >
      <span
        className={`mt-px grid h-8 w-8 shrink-0 place-items-center rounded-lg ${
          hot ? 'bg-orange/15 text-rust' : 'bg-brand-dim text-brand'
        }`}
      >
        <IntegrityIcon name={icon} className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold leading-snug text-ink">{label}</div>
        {sub && <div className="mt-0.5 text-[12.5px] leading-snug text-ink-muted">{sub}</div>}
        {detail}
      </div>
      {value && <div className="shrink-0 self-center">{value}</div>}
    </li>
  );
}
